<script lang="ts">
export interface DataTablePaginationProps {
	class?: any;
	infinite?: boolean;
	pagination?: PaginationState;
	defaultPageSize?: number;
	manual?: boolean;
	total?: number;
	hasMore?: boolean;
}
</script>

<script setup lang="ts">
import type { PaginationState } from "@tanstack/vue-table";
import { computed, onUnmounted, watch } from "vue";
import { cn } from "../../lib/utils";
import { useLocale } from "../../locale";
import { injectDataCollectionContext } from "../data-collection/composables/data-collection";
import InfinitePagination from "../pagination/InfinitePagination.vue";
import Pagination from "../pagination/Pagination.vue";
import Select from "../select/Select.vue";
import SelectOption from "../select/SelectOption.vue";
import { injectDataTableRootContext } from "./DataTableRoot.vue";
import { useStateModel } from "./state/state-model";

const props = defineProps<DataTablePaginationProps>();
const emit = defineEmits<{
	"update:pagination": [val: PaginationState];
}>();

const { manager, state } = injectDataCollectionContext();

const { pagination: paginationEnabled } = injectDataTableRootContext();

const pagination = useStateModel("pagination", props, emit, state);
if (props.defaultPageSize) {
	pagination.value.pageSize = props.defaultPageSize;
}

paginationEnabled.value = true;
onUnmounted(() => {
	paginationEnabled.value = false;
});

watch(
	() => props.manual,
	(isManual) => {
		manager.setOptions((opts) => ({ manualPagination: !!isManual, ...opts }));
	}
);

// const paginationItems = ["10", "20", "30", "40", "50", "All"];
const paginationItems = ["10", "20", "30", "40", "50"];

const { t } = useLocale();

const pageSize = computed(() => {
	if (!paginationEnabled.value) {
		return "All";
	}

	return pagination.value.pageSize.toString();
});

function setPageSize(size: string | "All" | null) {
	if (size === "All") {
		paginationEnabled.value = false;
	} else {
		if (!paginationEnabled.value) {
			paginationEnabled.value = true;
		}

		manager.setPageSize(Number(size));
	}
}
</script>

<template>
	<div :class="cn('flex flex-wrap items-center gap-2', props.class)">
		<div class="hidden items-center gap-2 md:flex">
			<div class="whitespace-nowrap">{{ t("table.rows-per-page") }}</div>
			<Select class="w-20" size="sm" :modelValue="pageSize" @update:modelValue="setPageSize">
				<SelectOption v-for="item of paginationItems" :key="item" :value="item">
					{{ item }}
				</SelectOption>
			</Select>
		</div>
		<InfinitePagination
			v-if="props.infinite"
			:page="pagination.pageIndex + 1"
			:hasMore="hasMore"
			@update:page="(p) => manager.setPageIndex(p - 1)"
		/>
		<Pagination
			v-else
			:siblingCount="1"
			:itemsPerPage="pagination.pageSize"
			:page="pagination.pageIndex + 1"
			:total="total ?? manager.getFilteredRowModel().rows.length"
			@update:page="(p) => manager.setPageIndex(p - 1)"
		/>
	</div>
</template>
